import { motion, AnimatePresence } from "framer-motion";
import {X, Rocket, CheckCircle2, Sparkles} from 'lucide-react';
import { useState, useEffect } from "react";
import { gradients } from "../../styles/gradients";
import { submitLeadToGHL, captureUTMParams } from "../../services/ghlApi";
import { typography, textColors } from "../../styles/typography";

interface ActivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  preSelectedPlan?: string;
}

export const ActivationModal = ({ isOpen, onClose, preSelectedPlan }: ActivationModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    plan: preSelectedPlan || "enterprise-anual",
    message: ""
  });

  // Actualizar plan cuando cambia preSelectedPlan
  useEffect(() => {
    if (preSelectedPlan) {
      setFormData(prev => ({ ...prev, plan: preSelectedPlan }));
    }
  }, [preSelectedPlan]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Track Lead event in Facebook Pixel
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead', {
        content_name: `Plan: ${formData.plan}`,
        content_category: 'activation_modal',
        value: formData.plan === 'enterprise-mensual' ? 144 : formData.plan === 'enterprise-anual' ? 1733 : formData.plan === 'starter-mensual' ? 29 : 290,
        currency: 'USD'
      });
    }
    
    // Mapeo de planes a links de pago - Dominio GHL Brand
    const paymentLinks: { [key: string]: string } = {
      "starter-mensual": "https://venta.zoefel-ia.com/payment-link/6910da1ba5cfde118dce57ce",
      "starter-anual": "https://venta.zoefel-ia.com/payment-link/6910f7acac56fe3e8eff4146",
      "enterprise-mensual": "https://venta.zoefel-ia.com/payment-link/6910f7ddac56fe63b1ff4195",
      "enterprise-anual": "https://venta.zoefel-ia.com/payment-link/6910f83665afca523f040470"
    };
    
    // Capturar parámetros UTM para tracking
    const utmParams = captureUTMParams();
    
    // Enviar lead a GoHighLevel - INTEGRACIÓN COMPLETA
    const ghlResult = await submitLeadToGHL({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      plan: formData.plan,
      message: formData.message,
      source: "funnel",
      ...utmParams
    });
    
    // Log del resultado (en producción enviar a analytics)
    if (ghlResult.success) {
      console.log("✅ Lead registrado en GHL:", ghlResult.contactId);
      console.log("✅ Oportunidad creada:", ghlResult.opportunityId);
      
      // Track successful GHL submission
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('trackCustom', 'GHLLeadCreated', {
          contact_id: ghlResult.contactId,
          opportunity_id: ghlResult.opportunityId,
          plan: formData.plan
        });
      }
    } else {
      console.error("❌ Error al registrar en GHL:", ghlResult.error);
      // Continuar con el flujo aunque falle GHL (no bloquear al usuario)
    }
    
    setIsSubmitting(false);
    setIsSuccess(true);
    
    // Abrir link de pago según el plan seleccionado
    const selectedPaymentLink = paymentLinks[formData.plan];
    if (selectedPaymentLink && formData.plan !== "trial-free") {
      // Track InitiateCheckout event before redirecting
      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'InitiateCheckout', {
          content_name: formData.plan,
          content_category: 'payment_redirect',
          value: formData.plan === 'enterprise-mensual' ? 144 : formData.plan === 'enterprise-anual' ? 1733 : formData.plan === 'starter-mensual' ? 29 : 290,
          currency: 'USD'
        });
      }
      
      // Abrir en nueva ventana después de mostrar mensaje de éxito
      setTimeout(() => {
        window.open(selectedPaymentLink, '_blank', 'noopener,noreferrer');
      }, 1000);
    }
    
    // Resetear después de 3 segundos
    setTimeout(() => {
      setIsSuccess(false);
      onClose();
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        plan: "enterprise-anual",
        message: ""
      });
    }, 3000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4 py-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative bg-white rounded-3xl max-w-2xl w-full shadow-2xl my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header con gradiente */}
          <div className={`${gradients.primary} p-8 rounded-t-3xl relative overflow-hidden`}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute top-4 right-4 opacity-20"
            >
              <Sparkles className="w-32 h-32 text-white" />
            </motion.div>
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center transition-all z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="relative z-10">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Rocket className="w-16 h-16 text-white mx-auto mb-4" />
              </motion.div>
              <h2 className={`${typography.h2Light} text-center mb-2`}>
                {preSelectedPlan === 'trial-free' ? '🎁 Prueba Gratis 15 Días' : '🚀 Sistema Activo en 72h'}
              </h2>
              <p className={`${typography.bodyLarge} ${textColors.primaryLight} text-center opacity-90`}>
                {preSelectedPlan === 'trial-free' ? 'Sin tarjeta • Activa en minutos' : 'Listo para vender mientras duermes'}
              </p>
            </div>
          </div>

          {/* Formulario */}
          <div className="p-8">
            {!isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nombre */}
                <div>
                  <label htmlFor="name" className={`block ${typography.label} ${textColors.secondary} mb-2`}>
                    ¿Cómo te llamas? *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all"
                    placeholder="Tu nombre completo"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className={`block ${typography.label} ${textColors.secondary} mb-2`}>
                    Email (acceso al sistema) *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all"
                    placeholder="tu@email.com"
                  />
                </div>

                {/* Teléfono */}
                <div>
                  <label htmlFor="phone" className={`block ${typography.label} ${textColors.secondary} mb-2`}>
                    WhatsApp (soporte directo) *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all"
                    placeholder="+52 55 1234 5678"
                  />
                </div>

                {/* Empresa */}
                <div>
                  <label htmlFor="company" className={`block ${typography.label} ${textColors.secondary} mb-2`}>
                    Tu negocio *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all"
                    placeholder="Nombre de tu empresa"
                  />
                </div>

                {/* Plan */}
                <div>
                  <label htmlFor="plan" className={`block ${typography.label} ${textColors.secondary} mb-2`}>
                    Elige tu plan *
                  </label>
                  <select
                    id="plan"
                    name="plan"
                    required
                    value={formData.plan}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all bg-white"
                  >
                    <option value="trial-free">🎁 Prueba 15 Días Gratis (Sin Tarjeta)</option>
                    <option value="starter-mensual">Starter Mensual → $29/mes</option>
                    <option value="starter-anual">Starter Anual → $290/año (ahorra $58)</option>
                    <option value="enterprise-mensual">Enterprise Mensual → $170/mes (50% OFF)</option>
                    <option value="enterprise-anual">Enterprise Anual → $1,920/año (MÁS ELEGIDO - ahorra $1,920)</option>
                  </select>
                </div>

                {/* Calificación GHL - Optimizado */}
                <div className="bg-indigo-50 rounded-xl p-4 border-2 border-indigo-100">
                  <p className="text-xs font-bold text-indigo-900 mb-3 uppercase tracking-wide">📊 Personaliza tu sistema (30 seg)</p>
                  
                  <label htmlFor="message" className={`block ${typography.label} ${textColors.secondary} mb-2`}>
                    Volumen mensual de leads *
                  </label>
                  <select
                    id="leadsPerMonth"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-indigo-200 rounded-xl focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all bg-white mb-4"
                  >
                    <option value="">Selecciona tu rango</option>
                    <option value="0-50">Empezando (0-50 leads)</option>
                    <option value="51-200">Creciendo (51-200 leads)</option>
                    <option value="201-500">Escalando (201-500 leads)</option>
                    <option value="500+">Alto volumen (+500 leads)</option>
                  </select>
                  
                  <label htmlFor="revenue" className={`block ${typography.label} ${textColors.secondary} mb-2`}>
                    Ticket promedio por venta *
                  </label>
                  <select
                    id="revenue"
                    name="revenue"
                    required
                    className="w-full px-4 py-3 border-2 border-indigo-200 rounded-xl focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 outline-none transition-all bg-white"
                  >
                    <option value="">Selecciona tu rango</option>
                    <option value="<50">Producto bajo (&lt;$50)</option>
                    <option value="50-250">Ticket medio ($50-$250)</option>
                    <option value="250-1000">Ticket alto ($250-$1k)</option>
                    <option value="1000+">Premium (+$1,000)</option>
                  </select>
                </div>

                {/* Botón Submit */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full ${gradients.primary} text-white font-bold text-lg py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3`}
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-6 h-6 border-3 border-white border-t-transparent rounded-full"
                      />
                      Confirmando...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-6 h-6" />
                      {preSelectedPlan === 'trial-free' ? 'Activar Prueba Gratis' : 'Activar Sistema → Ir a Pago'}
                    </>
                  )}
                </motion.button>

                {/* Garantía - Compacto */}
                <div className="text-center pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    ⚡ Activo en 72h • 💰 Garantía 30 días • 🔒 100% Seguro
                  </p>
                </div>
              </form>
            ) : (
              // Mensaje de éxito - Optimizado
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-12"
              >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5 }}
                >
                  <CheckCircle2 className="w-24 h-24 text-emerald-500 mx-auto mb-6" />
                </motion.div>
                <h3 className={`${typography.h2} mb-4`}>
                  ¡Sistema Activado! 🎉
                </h3>
                <p className={`${typography.bodyLarge} ${textColors.secondary} mb-2`}>
                  Nos conectamos contigo en <span className="font-bold text-indigo-600">&lt;24 horas</span>
                </p>
                <p className={`${typography.bodyBase} ${textColors.tertiary}`}>
                  📧 Revisa tu email • 💬 WhatsApp listo
                </p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
